'use client'

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useStore } from 'zustand'
import { type StoreApi } from 'zustand/vanilla'
import { createCreationStore, type CreationState, type CreationStoreInit } from './create-store'

const CreationStoreContext = createContext<StoreApi<CreationState> | null>(null)

type Props = Omit<CreationStoreInit, 'navigate' | 'onRequireLogin'> & {
    children: ReactNode
    navigate: (url: string) => void
    onRequireLogin: () => void
}

export const CreationStoreProvider = ({ children, ...init }: Props) => {
    // Created per mount, never at module scope: the store must die with the
    // layout so sources and their canvases are released on leaving the tool.
    const [store] = useState(() => createCreationStore(init))
    const storeRef = useRef(store)

    useEffect(() => {
        const current = storeRef.current
        return () => current.getState().reset()
    }, [])

    // isLoggedIn is captured at construction time but can change afterward
    // (e.g. a server-derived prop update on sign-in); keep the store's copy
    // in sync so a blocked setFiles retry is not stuck forever.
    useEffect(() => {
        storeRef.current.getState().setIsLoggedIn(init.isLoggedIn)
    }, [init.isLoggedIn])

    // Same defect class as isLoggedIn above: userRestrictionAccess is
    // captured once at construction but a mid-flow sign-in/verification
    // hands CreationShell a fresh, correct list on the next server render.
    // The server recomputes this array on every render, so a naive
    // `[init.userRestrictionAccess]` dependency would fire on every render
    // regardless of content — keying on the joined contents instead only
    // re-syncs the store when the actual restriction set changes.
    useEffect(() => {
        storeRef.current.getState().setUserRestrictionAccess(init.userRestrictionAccess)
    }, [init.userRestrictionAccess.join('|')])

    return <CreationStoreContext.Provider value={store}>{children}</CreationStoreContext.Provider>
}

export function useCreationStore<T>(selector: (state: CreationState) => T): T {
    const store = useContext(CreationStoreContext)
    if (!store) {
        throw new Error('useCreationStore must be used inside a CreationStoreProvider')
    }
    return useStore(store, selector)
}
